import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.solteros.it/speeddate/api';

export default function BillingPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    axios.get(`${API_URL}/billing`, {
      headers: { 'x-admin-token': token }
    })
    .then(res => setData(res.data))
    .catch(err => setError(err.message))
    .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="card border-0 shadow-sm glass">
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Caricamento...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card border-0 shadow-sm glass">
        <div className="card-body text-center py-5">
          <i className="bi bi-exclamation-triangle text-danger fs-1"></i>
          <p className="text-danger mt-3">Errore nel caricamento dei dati di billing</p>
          <p className="text-muted small">{error}</p>
        </div>
      </div>
    );
  }

  const yearlyTotals = data.reduce((acc, q) => {
    acc.tickets += q.tickets;
    acc.fixedCost += q.fixedCost;
    acc.variableCost += q.variableCost;
    acc.totalCost += q.totalCost;
    return acc;
  }, { tickets: 0, fixedCost: 0, variableCost: 0, totalCost: 0 });

  const maxTickets = Math.max(...data.map(q => q.tickets), 1);

  return (
    <div className="container-fluid">
      <div className="row g-4">
        {/* Summary Cards */}
        <div className="col-12">
          <h4 className="fw-bold mb-4">
            <i className="bi bi-calculator"></i> Fatturazione Trimestrale
          </h4>
        </div>

        <div className="col-md-3">
          <div className="card border-0 shadow-sm glass h-100">
            <div className="card-body text-center">
              <h6 className="text-muted small text-uppercase mb-2">Biglietti Totali</h6>
              <h2 className="fw-bold text-primary mb-0">{yearlyTotals.tickets}</h2>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card border-0 shadow-sm glass h-100">
            <div className="card-body text-center">
              <h6 className="text-muted small text-uppercase mb-2">Costo Fisso</h6>
              <h2 className="fw-bold text-info mb-0">€{yearlyTotals.fixedCost.toFixed(2)}</h2>
              <small className="text-muted">€48/mese</small>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card border-0 shadow-sm glass h-100">
            <div className="card-body text-center">
              <h6 className="text-muted small text-uppercase mb-2">Costo Variabile</h6>
              <h2 className="fw-bold text-warning mb-0">€{yearlyTotals.variableCost.toFixed(2)}</h2>
              <small className="text-muted">€0.50-1.00/biglietto</small>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card border-0 shadow-sm glass h-100">
            <div className="card-body text-center">
              <h6 className="text-muted small text-uppercase mb-2">Totale Annuale</h6>
              <h2 className="fw-bold text-success mb-0">€{yearlyTotals.totalCost.toFixed(2)}</h2>
            </div>
          </div>
        </div>

        {/* Quarterly Table */}
        <div className="col-12">
          <div className="card border-0 shadow-sm glass">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="small text-uppercase fw-bold text-muted py-3 ps-4">Trimestre</th>
                      <th className="small text-uppercase fw-bold text-muted py-3">Periodo</th>
                      <th className="small text-uppercase fw-bold text-muted py-3 text-center">Biglietti</th>
                      <th className="small text-uppercase fw-bold text-muted py-3 text-end">Costo Fisso</th>
                      <th className="small text-uppercase fw-bold text-muted py-3 text-end">Costo Variabile</th>
                      <th className="small text-uppercase fw-bold text-muted py-3 text-end pe-4">Totale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((q, i) => (
                      <tr key={i}>
                        <td className="fw-bold ps-4 py-3">{q.quarter}</td>
                        <td className="text-muted small">
                          {new Date(q.start).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })} - 
                          {' '}{new Date(q.end).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="text-center">
                          <span className="badge bg-primary rounded-pill">{q.tickets}</span>
                          <div className="progress mt-2" style={{height: '4px'}}>
                            <div 
                              className="progress-bar" 
                              style={{width: `${(q.tickets / maxTickets) * 100}%`}}
                            ></div>
                          </div>
                        </td>
                        <td className="text-end text-info">€{q.fixedCost.toFixed(2)}</td>
                        <td className="text-end text-warning">€{q.variableCost.toFixed(2)}</td>
                        <td className="text-end fw-bold pe-4">€{q.totalCost.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="table-light">
                    <tr className="fw-bold">
                      <td className="ps-4 py-3" colSpan={2}>TOTALE ANNUALE</td>
                      <td className="text-center">
                        <span className="badge bg-dark rounded-pill">{yearlyTotals.tickets}</span>
                      </td>
                      <td className="text-end text-info">€{yearlyTotals.fixedCost.toFixed(2)}</td>
                      <td className="text-end text-warning">€{yearlyTotals.variableCost.toFixed(2)}</td>
                      <td className="text-end text-success pe-4">€{yearlyTotals.totalCost.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Info */}
        <div className="col-12">
          <div className="card border-0 shadow-sm glass">
            <div className="card-body">
              <h6 className="fw-bold mb-3">
                <i className="bi bi-info-circle"></i> Struttura dei Prezzi
              </h6>
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="p-3 bg-light rounded">
                    <strong className="text-info">Costo Fisso Mensile</strong>
                    <p className="mb-0 mt-2 small">€48 (€36 hosting + €12 backup)</p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="p-3 bg-light rounded">
                    <strong className="text-warning">Primo Tier (0-500 biglietti)</strong>
                    <p className="mb-0 mt-2 small">€0.50 per biglietto</p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="p-3 bg-light rounded">
                    <strong className="text-danger">Secondo Tier (500+ biglietti)</strong>
                    <p className="mb-0 mt-2 small">€1.00 per biglietto</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
